import { createApi } from '@reduxjs/toolkit/query/react';

import { isMockCaseEnabled } from '#shared';

import type { ActiveCase } from '#entities/types';

import {
  fetchWithAuthBaseQuery,
  validateAndTransformResponse,
} from '#shared/api';
import type { DataWithValidation } from '#shared/api';

import { loadDemoActiveCasesSource } from './demoSource';
import { activeCasesResponseSchema, type ActiveCasesResponse } from './schemas';

const paths = {
  activeCases: 'api/activeCases',
} as const;

const transform = (response: ActiveCasesResponse) =>
  validateAndTransformResponse(
    activeCasesResponseSchema,
    response,
    value => value?.label,
    'activeCases',
  );

export const activeCasesApi = createApi({
  reducerPath: 'activeCasesApi',
  baseQuery: fetchWithAuthBaseQuery,
  refetchOnReconnect: true,
  endpoints: builder => ({
    getActiveCases: builder.query<DataWithValidation<ActiveCase[]>, void>({
      // `queryFn`, not `query`: a 5xx throws before `transformResponse` (E038/E039).
      queryFn: async (_arg, _api, _extra, baseQuery) => {
        const buildDemo = isMockCaseEnabled()
          ? await loadDemoActiveCasesSource()
          : null;
        if (buildDemo) {
          return { data: transform(buildDemo(Date.now())) };
        }

        const result = await baseQuery(paths.activeCases);

        return result.error
          ? { error: result.error }
          : { data: transform(result.data as ActiveCasesResponse) };
      },
    }),
  }),
});

export const { useGetActiveCasesQuery } = activeCasesApi;
